/**
 * Orthopedic CPT Proof Map Starter Pack
 * 
 * Rule-based, human-defined evidence mapping for common
 * orthopedic procedures used in Dr. Larkin's practice.
 * 
 * SHOULDER: 29827, 29826, 29824, 29828, 29807, 29822, 29823
 * KNEE: 29881, 29882, 29877, 27447
 * ASSISTANT SURGEON: 09880
 */

import { CptProofMap, EvidenceRule, ProofMapRequirementElement } from './types';

// ============================================================================
// HELPER: Build a simple keyword rule
// ============================================================================

function keywordRule(
  id: string,
  patterns: string[],
  negatives: string[] = [],
  note: string = '',
  confidence: 'HIGH' | 'MEDIUM' | 'LOW' = 'HIGH',
  context?: { term: string; max_distance_words: number },
): EvidenceRule {
  return {
    rule_id: id,
    match_type: 'KEYWORD',
    patterns,
    negative_patterns: negatives,
    required_context: context,
    min_hits: 1,
    confidence,
    note: note || `Matched keyword from: ${patterns.slice(0, 3).join(', ')}`,
  };
}

// ============================================================================
// SHOULDER ARTHROSCOPY
// ============================================================================

const CPT_29827: CptProofMap = {
  cpt_code: '29827',
  cpt_description: 'Arthroscopy, shoulder, surgical; with rotator cuff repair',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29827_repair_performed',
      label: 'Rotator Cuff Repair Performed',
      description: 'Documentation that a rotator cuff repair was performed using anchors or sutures',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29827_r1', 
          ['rotator cuff repair', 'repair of rotator cuff', 'cuff repaired', 'cuff repair'],
          ['no repair performed', 'repair not performed', 'cuff not repaired'],
          'Matched rotator cuff repair documentation'
        ),
        keywordRule('29827_r2',
          ['suture anchor', 'anchors placed', 'speedbridge', 'mattress sutures', 'anchor fixation'],
          [],
          'Matched repair technique (anchor/suture method)',
          'HIGH'
        ),
        keywordRule('29827_r3',
          ['tendon secured', 'tendon reattached', 'footprint preparation', 'tendon reduced'],
          [],
          'Matched tendon fixation language',
          'MEDIUM'
        ),
      ],
    },
    {
      requirement_id: '29827_tear_identified',
      label: 'Rotator Cuff Tear Identified',
      description: 'Documentation of rotator cuff tear pathology',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29827_t1',
          ['rotator cuff tear', 'cuff tear', 'supraspinatus tear', 'infraspinatus tear', 
           'subscapularis tear', 'full-thickness tear', 'partial-thickness tear', 'full thickness tear'],
          ['no tear identified', 'no cuff tear', 'intact rotator cuff'],
          'Matched rotator cuff tear identification'
        ),
      ],
    },
    {
      requirement_id: '29827_arthroscopic',
      label: 'Arthroscopic Approach',
      description: 'Confirmation procedure was performed arthroscopically',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29827_a1',
          ['arthroscopy', 'arthroscopic', 'arthroscopically', 'scope', 'portal', 'portals established'],
          [],
          'Matched arthroscopic approach'
        ),
      ],
    },
  ],
};

const CPT_29826: CptProofMap = {
  cpt_code: '29826',
  cpt_description: 'Arthroscopy, shoulder, surgical; decompression of subacromial space',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29826_decompression',
      label: 'Subacromial Decompression Performed',
      description: 'Documentation that decompression was performed in the subacromial space',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29826_d1',
          ['subacromial decompression', 'acromioplasty', 'acromion resection', 'acromial spur removal'],
          ['no decompression', 'decompression not performed', 'no acromioplasty'],
          'Matched subacromial decompression procedure',
          'HIGH',
          { term: 'subacromial', max_distance_words: 15 },
        ),
        keywordRule('29826_d2',
          ['bursectomy', 'bursa debrided', 'subacromial bursa removed'],
          ['bursectomy only'],
          'Matched bursectomy in subacromial space (may be ambiguous alone)',
          'MEDIUM'
        ),
        keywordRule('29826_d3',
          ['shaver used in subacromial space', 'burr in subacromial', 'subacromial space visualized'],
          [],
          'Matched instrumentation in subacromial space',
          'MEDIUM'
        ),
      ],
    },
    {
      requirement_id: '29826_separate_work',
      label: 'Distinct Work Beyond Cuff Repair',
      description: 'Documentation that work in subacromial space was separate/distinct from primary procedure',
      strictness: 'CONDITIONAL',
      evidence_rules: [
        keywordRule('29826_s1',
          ['subacromial space', 'separate compartment', 'separate anatomic site', 'distinct procedure',
           'entered the subacromial space', 'subacromial bursa'],
          [],
          'Matched separate compartment / distinct work documentation',
          'HIGH'
        ),
      ],
    },
  ],
};

const CPT_29824: CptProofMap = {
  cpt_code: '29824',
  cpt_description: 'Arthroscopy, shoulder, surgical; distal claviculectomy',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29824_excision',
      label: 'Distal Clavicle Excision Performed',
      description: 'Documentation of distal clavicle resection/excision',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29824_e1',
          ['distal clavicle excision', 'distal clavicle resection', 'mumford', 'mumford procedure',
           'ac joint resection', 'acromioclavicular resection', 'distal claviculectomy'],
          ['no distal clavicle excision', 'clavicle excision not performed'],
          'Matched distal clavicle excision documentation'
        ),
      ],
    },
  ],
};

const CPT_29828: CptProofMap = {
  cpt_code: '29828',
  cpt_description: 'Arthroscopy, shoulder, surgical; biceps tenodesis',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29828_tenodesis',
      label: 'Biceps Tenodesis Performed',
      description: 'Documentation that biceps tenodesis (not tenotomy) was performed',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29828_t1',
          ['biceps tenodesis', 'tenodesed', 'biceps fixed', 'tenodesis performed',
           'biceps tendon secured', 'subpectoral tenodesis'],
          ['tenotomy', 'biceps tenotomy', 'tenodesis not performed'],
          'Matched biceps tenodesis — note: tenotomy is a negation for tenodesis'
        ),
        keywordRule('29828_t2',
          ['interference screw', 'anchor tenodesis', 'tenodesis screw', 'biceps anchor'],
          [],
          'Matched tenodesis fixation method',
          'HIGH'
        ),
      ],
    },
  ],
};

const CPT_29807: CptProofMap = {
  cpt_code: '29807',
  cpt_description: 'Arthroscopy, shoulder, surgical; repair of SLAP lesion',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29807_slap_repair',
      label: 'SLAP Repair Performed',
      description: 'Documentation of labral repair for SLAP lesion',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29807_s1',
          ['slap repair', 'labrum repaired', 'labral repair', 'superior labrum repair',
           'suture anchor in superior labrum', 'labral refixation'],
          ['slap lesion not repaired', 'labrum intact', 'no labral repair'],
          'Matched SLAP / labral repair documentation'
        ),
      ],
    },
    {
      requirement_id: '29807_slap_identified',
      label: 'SLAP Lesion Identified',
      description: 'Documentation that a SLAP lesion was identified',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29807_i1',
          ['slap lesion', 'slap tear', 'superior labral tear', 'type ii slap', 'type iii slap',
           'type iv slap', 'superior labral pathology'],
          ['no slap lesion', 'labrum intact'],
          'Matched SLAP lesion identification'
        ),
      ],
    },
  ],
};

const CPT_29822: CptProofMap = {
  cpt_code: '29822',
  cpt_description: 'Arthroscopy, shoulder, surgical; debridement, limited',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29822_debridement',
      label: 'Limited Debridement Performed',
      description: 'Documentation of limited debridement with site specified',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29822_d1',
          ['debridement', 'debrided', 'debride', 'shaver used to debride', 'synovectomy',
           'tissue removed', 'loose body removed'],
          ['no debridement', 'debridement not performed'],
          'Matched debridement documentation'
        ),
      ],
    },
    {
      requirement_id: '29822_site',
      label: 'Debridement Site Documented',
      description: 'Payer may require specific anatomic site for debridement',
      strictness: 'CONDITIONAL',
      evidence_rules: [
        keywordRule('29822_site1',
          ['labrum', 'labral', 'bursa', 'bursectomy', 'glenohumeral', 'subacromial',
           'biceps', 'rotator cuff', 'articular surface', 'synovium'],
          [],
          'Matched debridement site (anatomic location)',
          'MEDIUM'
        ),
      ],
    },
  ],
};

const CPT_29823: CptProofMap = {
  cpt_code: '29823',
  cpt_description: 'Arthroscopy, shoulder, surgical; debridement, extensive',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29823_extensive',
      label: 'Extensive Debridement Performed',
      description: 'Documentation of extensive debridement (exceeding 30 minutes or multiple sites)',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29823_e1',
          ['extensive debridement', 'extensive shaving', 'exceeding 30 minutes', 'significant debridement',
           'debridement throughout', 'multiple areas debrided'],
          ['limited debridement', 'minor debridement', 'less than 30 minutes'],
          'Matched extensive debridement documentation'
        ),
        keywordRule('29823_e2',
          ['debrided', 'debridement', 'shaver', 'synovectomy'],
          [],
          'Matched general debridement (requires extensive qualifier for HIGH confidence)',
          'LOW'
        ),
      ],
    },
  ],
};

// ============================================================================
// KNEE
// ============================================================================

const CPT_27570: CptProofMap = {
  cpt_code: '27570',
  cpt_description: 'Manipulation of knee joint under general anesthesia',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '27570_anesthesia',
      label: 'General Anesthesia / Muscle Relaxation',
      description: 'Documentation that manipulation was performed under anesthesia with relaxation',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27570_a1',
          ['general anesthesia', 'complete muscle relaxation', 'under anesthesia'],
          [],
          'Matched anesthesia and relaxation documentation',
        ),
      ],
    },
    {
      requirement_id: '27570_pre_rom',
      label: 'Pre-Manipulation ROM Documented',
      description: 'Pre-manipulation range of motion or contracture severity is documented',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27570_rom1',
          ['pre-manipulation range of motion', 'rigidly restricted', 'fixed flexion limitation', 'flexion contracture'],
          [],
          'Matched pre-manipulation ROM/contracture documentation',
        ),
      ],
    },
    {
      requirement_id: '27570_manipulation',
      label: 'Manipulation / Adhesion Release Performed',
      description: 'Documentation of manipulation technique and lysis of adhesions',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27570_m1',
          ['sustained flexion pressure', 'lysis of fibrotic adhesions', 'audible and palpable lysis', 'manipulation was performed'],
          [],
          'Matched manipulation and adhesion release documentation',
        ),
      ],
    },
    {
      requirement_id: '27570_post_rom',
      label: 'Post-Manipulation ROM And Safety',
      description: 'Post-manipulation range of motion and absence of complication are documented',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27570_post1',
          ['post-manipulation range of motion', 'full extension', 'no evidence of fracture', 'no ligamentous compromise'],
          [],
          'Matched post-manipulation ROM and safety documentation',
        ),
      ],
    },
  ],
};

const CPT_27385: CptProofMap = {
  cpt_code: '27385',
  cpt_description: 'Primary repair of ruptured quadriceps or hamstring muscle',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '27385_pathology',
      label: 'Extensor Mechanism Disruption Identified',
      description: 'Documentation identifies traumatic quadriceps/extensor mechanism disruption',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27385_p1',
          ['traumatic disruption', 'extensor mechanism incompetence', 'quadriceps tendon and VMO', 'distal quadriceps tendon'],
          [],
          'Matched extensor mechanism pathology documentation',
        ),
      ],
    },
    {
      requirement_id: '27385_exposure',
      label: 'Open Exposure And Anatomy',
      description: 'Documentation of open exposure to the disrupted extensor mechanism',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27385_e1',
          ['longitudinal anterior knee incision', 'disrupted quadriceps/extensor mechanism', 'tendon edges were mobilized'],
          [],
          'Matched open exposure and tendon mobilization documentation',
        ),
      ],
    },
    {
      requirement_id: '27385_repair',
      label: 'Primary Tendon Repair Technique',
      description: 'Documentation of primary repair technique with durable suture fixation',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27385_r1',
          ['heavy nonabsorbable sutures', 'locking configuration', 'repaired back to the superior pole', 'restoration of continuity and tension'],
          [],
          'Matched primary repair technique documentation',
        ),
      ],
    },
    {
      requirement_id: '27385_stability',
      label: 'Repair Stability Confirmed',
      description: 'Documentation that repair stability was tested after fixation',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27385_s1',
          ['gentle range of motion', 'stable without gapping', 'repair was taken through', 'knee was placed in extension'],
          [],
          'Matched repair stability documentation',
        ),
      ],
    },
  ],
};

const CPT_29881: CptProofMap = {
  cpt_code: '29881',
  cpt_description: 'Arthroscopy, knee, surgical; with meniscectomy (medial OR lateral)',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29881_meniscectomy',
      label: 'Partial Meniscectomy Performed',
      description: 'Documentation of partial meniscectomy with compartment specified',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29881_m1',
          ['partial medial meniscectomy', 'partial lateral meniscectomy', 'meniscus trimmed',
           'meniscal resection', 'meniscectomy', 'meniscus resected', 'meniscal tissue removed',
           'partial meniscectomy'],
          ['no meniscectomy', 'meniscectomy not performed', 'meniscus intact'],
          'Matched partial meniscectomy documentation'
        ),
      ],
    },
    {
      requirement_id: '29881_compartment',
      label: 'Compartment Specified',
      description: 'Documentation of which compartment (medial or lateral)',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29881_c1',
          ['medial meniscus', 'lateral meniscus', 'medial compartment', 'lateral compartment',
           'posterior horn medial', 'posterior horn lateral', 'anterior horn medial', 'anterior horn lateral'],
          [],
          'Matched meniscectomy compartment specification'
        ),
      ],
    },
  ],
};

const CPT_29880: CptProofMap = {
  cpt_code: '29880',
  cpt_description: 'Arthroscopy, knee, surgical; with meniscectomy, medial AND lateral',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29880_arthroscopic',
      label: 'Arthroscopic Knee Approach',
      description: 'Documentation that the meniscectomy was performed arthroscopically',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29880_a1',
          ['diagnostic arthroscopy', 'arthroscope', 'standard portals', 'arthroscopic evaluation'],
          [],
          'Matched arthroscopic knee approach documentation',
        ),
      ],
    },
    {
      requirement_id: '29880_medial_lateral_tears',
      label: 'Medial And Lateral Meniscus Tears Identified',
      description: 'Both medial and lateral meniscal pathology must be documented',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29880_t1',
          ['medial meniscus tear', 'lateral meniscus tear', 'medial and lateral meniscus tears', 'medial and lateral meniscal tears'],
          [],
          'Matched both medial and lateral meniscal tears',
        ),
      ],
    },
    {
      requirement_id: '29880_meniscectomy',
      label: 'Partial Medial And Lateral Meniscectomy Performed',
      description: 'Documentation that unstable torn tissue was resected in both compartments',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29880_m1',
          ['partial medial meniscectomy', 'partial lateral meniscectomy', 'unstable meniscal tissue was resected', 'stable peripheral rims'],
          [],
          'Matched medial and lateral partial meniscectomy documentation',
        ),
      ],
    },
  ],
};

const CPT_29882: CptProofMap = {
  cpt_code: '29882',
  cpt_description: 'Arthroscopy, knee, surgical; with meniscus repair',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29882_repair',
      label: 'Meniscus Repair Performed',
      description: 'Documentation of meniscus repair technique',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29882_r1',
          ['meniscus repair', 'meniscal repair', 'all-inside repair', 'inside-out repair',
           'outside-in repair', 'sutured meniscus', 'meniscus sutured', 'meniscal suture'],
          ['no meniscus repair', 'repair not performed', 'meniscus not repairable'],
          'Matched meniscus repair documentation'
        ),
      ],
    },
    {
      requirement_id: '29882_technique',
      label: 'Repair Technique Documented',
      description: 'Documentation of specific repair technique used',
      strictness: 'CONDITIONAL',
      evidence_rules: [
        keywordRule('29882_tech1',
          ['all-inside', 'inside-out', 'outside-in', 'fast-fix', 'rapidloc', 'meniscal arrow',
           'vertical mattress', 'horizontal mattress suture'],
          [],
          'Matched specific repair technique',
          'HIGH'
        ),
      ],
    },
  ],
};

const CPT_29877: CptProofMap = {
  cpt_code: '29877',
  cpt_description: 'Arthroscopy, knee, surgical; debridement/shaving of articular cartilage (chondroplasty)',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '29877_chondroplasty',
      label: 'Chondroplasty Performed',
      description: 'Documentation of chondroplasty with location specified',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29877_c1',
          ['chondroplasty', 'cartilage debrided', 'chondral debridement', 'cartilage smoothing',
           'articular cartilage debridement', 'shaving of articular cartilage'],
          ['no chondroplasty', 'cartilage intact'],
          'Matched chondroplasty documentation'
        ),
        keywordRule('29877_c2',
          ['microfracture'],
          [],
          'Matched "microfracture" — note: microfracture is a different procedure (29879), mark ambiguous',
          'LOW'
        ),
      ],
    },
    {
      requirement_id: '29877_location',
      label: 'Chondroplasty Location',
      description: 'Location of chondroplasty (condyle, trochlea, patella)',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('29877_l1',
          ['medial femoral condyle', 'lateral femoral condyle', 'trochlea', 'patella',
           'tibial plateau', 'weight-bearing surface', 'condylar surface'],
          [],
          'Matched chondroplasty anatomic location'
        ),
      ],
    },
  ],
};

const CPT_27447: CptProofMap = {
  cpt_code: '27447',
  cpt_description: 'Arthroplasty, knee, condyle and plateau; medial AND lateral compartments (TKA)',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '27447_tka',
      label: 'Total Knee Arthroplasty Performed',
      description: 'Documentation of total knee arthroplasty procedure',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27447_t1',
          ['total knee arthroplasty', 'total knee replacement', 'tka', 'knee replacement',
           'knee prosthesis', 'knee implant'],
          ['no arthroplasty', 'arthroplasty not performed'],
          'Matched TKA procedure documentation'
        ),
      ],
    },
    {
      requirement_id: '27447_implants',
      label: 'Implant Components Referenced',
      description: 'Documentation of femoral and tibial components',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27447_i1',
          ['femoral component', 'tibial component', 'tibial baseplate', 'polyethylene insert',
           'polyethylene liner', 'patella component', 'femoral implant', 'tibial implant'],
          [],
          'Matched implant component documentation'
        ),
      ],
    },
    {
      requirement_id: '27447_both_compartments',
      label: 'Both Compartments Addressed',
      description: 'Documentation of medial and lateral compartment involvement',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27447_b1',
          ['medial and lateral', 'both compartments', 'medial compartment', 'lateral compartment'],
          [],
          'Matched both compartment documentation',
          'HIGH'
        ),
        keywordRule('27447_b2',
          ['femoral cut', 'tibial cut', 'tibial resection', 'femoral resection', 
           'distal femoral resection', 'tibial plateau resected'],
          [],
          'Matched bone preparation implying both compartments',
          'MEDIUM'
        ),
      ],
    },
  ],
};

const CPT_27435: CptProofMap = {
  cpt_code: '27435',
  cpt_description: 'Capsulotomy, posterior capsular release, knee',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '27435_contracture',
      label: 'Fixed Contracture Identified',
      description: 'Documentation identifies the fixed contracture requiring release',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27435_c1',
          ['fixed flexion contracture', 'posterior capsular tightness', 'extension gap limitation'],
          [],
          'Matched contracture indication documentation',
        ),
      ],
    },
    {
      requirement_id: '27435_release',
      label: 'Posterior Capsular Release Performed',
      description: 'Documentation of posterior capsular release and balancing work',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('27435_r1',
          ['posterior capsular release', 'collateral ligament balancing', 'flexion-extension gap', 'balanced extension gap'],
          [],
          'Matched posterior release and balancing documentation',
        ),
      ],
    },
    {
      requirement_id: '27435_distinct_work',
      label: 'Distinct Contracture-Balancing Work',
      description: 'Documentation separates release work from routine TKA bone preparation',
      strictness: 'CONDITIONAL',
      evidence_rules: [
        keywordRule('27435_d1',
          ['distinct contracture-balancing work', 'separate posterior capsular release', 'beyond routine exposure'],
          [],
          'Matched distinct work documentation',
        ),
      ],
    },
  ],
};

// ============================================================================
// ASSISTANT SURGEON
// ============================================================================

const CPT_23420: CptProofMap = {
  cpt_code: '23420',
  cpt_description: 'Reconstruction of complete shoulder rotator cuff avulsion, chronic',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '23420_open_exposure',
      label: 'Open Shoulder Exposure',
      description: 'Documentation of open exposure for chronic rotator cuff reconstruction',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23420_e1',
          ['open deltoid-splitting approach', 'deltoid was protected', 'subacromial space was exposed'],
          [],
          'Matched open shoulder exposure documentation',
        ),
      ],
    },
    {
      requirement_id: '23420_chronic_cuff',
      label: 'Chronic Rotator Cuff Tear Mobilized',
      description: 'Documentation of chronic cuff tear pathology and mobilization',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23420_c1',
          ['chronic full-thickness rotator cuff tear', 'cuff edges were mobilized', 'supraspinatus tendon was mobilized'],
          [],
          'Matched chronic cuff pathology and mobilization',
        ),
      ],
    },
    {
      requirement_id: '23420_acromioplasty',
      label: 'Acromioplasty / Outlet Decompression',
      description: 'Documentation of acromioplasty or subacromial decompression included in the open repair',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23420_a1',
          ['acromioplasty', 'subacromial decompression', 'anterior acromial spur', 'outlet decompression'],
          [],
          'Matched acromioplasty/outlet decompression documentation',
        ),
      ],
    },
    {
      requirement_id: '23420_repair',
      label: 'Rotator Cuff Repair Fixation',
      description: 'Documentation of cuff repair fixation to bone',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23420_r1',
          ['transosseous sutures', 'suture anchors', 'repaired to the greater tuberosity', 'tendon was secured to bone'],
          [],
          'Matched open cuff repair fixation documentation',
        ),
      ],
    },
  ],
};

const CPT_23120: CptProofMap = {
  cpt_code: '23120',
  cpt_description: 'Claviculectomy; partial',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '23120_ac_joint',
      label: 'AC Joint Pathology Addressed',
      description: 'Documentation that the acromioclavicular joint was the separate pain generator',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23120_ac1',
          ['acromioclavicular joint', 'AC joint arthrosis', 'separate AC joint pathology', 'distal clavicle osteophytes'],
          [],
          'Matched AC joint pathology documentation',
        ),
      ],
    },
    {
      requirement_id: '23120_resection',
      label: 'Distal Clavicle Resection Performed',
      description: 'Documentation of distal clavicle excision/resection',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('23120_r1',
          ['distal clavicle resection', 'distal clavicle excision', 'partial claviculectomy', 'approximately 8 mm of distal clavicle'],
          [],
          'Matched distal clavicle resection documentation',
        ),
      ],
    },
    {
      requirement_id: '23120_distinct_site',
      label: 'Separate AC Joint Site',
      description: 'Documentation separates AC joint work from cuff repair/outlet work',
      strictness: 'CONDITIONAL',
      evidence_rules: [
        keywordRule('23120_d1',
          ['separate acromioclavicular joint structure', 'distinct AC joint site', 'separate from the rotator cuff repair'],
          [],
          'Matched distinct AC joint site documentation',
        ),
      ],
    },
  ],
};

const CPT_64721: CptProofMap = {
  cpt_code: '64721',
  cpt_description: 'Neuroplasty and/or transposition; median nerve at carpal tunnel',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '64721_carpal_tunnel',
      label: 'Carpal Tunnel Exposure',
      description: 'Documentation of carpal tunnel incision/exposure',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('64721_e1',
          ['incision was made over the carpal tunnel', 'palmar incision', 'carpal tunnel was exposed'],
          [],
          'Matched carpal tunnel exposure documentation',
        ),
      ],
    },
    {
      requirement_id: '64721_release',
      label: 'Transverse Carpal Ligament Released',
      description: 'Documentation of complete transverse carpal ligament release',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('64721_r1',
          ['transverse carpal ligament was completely released', 'complete release of the transverse carpal ligament', 'release was carried distally and proximally'],
          [],
          'Matched transverse carpal ligament release',
        ),
      ],
    },
    {
      requirement_id: '64721_median_nerve',
      label: 'Median Nerve Decompressed / Neuroplasty',
      description: 'Documentation that the median nerve was visualized and decompressed',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('64721_n1',
          ['median nerve was visualized', 'median nerve decompressed', 'median neuroplasty', 'nerve was freely decompressed'],
          [],
          'Matched median nerve decompression documentation',
        ),
      ],
    },
  ],
};

const CPT_20610: CptProofMap = {
  cpt_code: '20610',
  cpt_description: 'Arthrocentesis, aspiration and/or injection, major joint or bursa',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '20610_joint_identified',
      label: 'Major Joint Identified',
      description: 'Documentation identifies the major joint or bursa treated',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('20610_j1',
          ['major joint injection', 'knee joint injection', 'subacromial injection', 'glenohumeral injection'],
          [],
          'Matched major joint site documentation',
        ),
      ],
    },
    {
      requirement_id: '20610_injection_performed',
      label: 'Injection / Aspiration Performed',
      description: 'Documentation that injection or aspiration was performed',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('20610_i1',
          ['injection was performed', 'aspiration was performed', 'medication was injected', 'standard sterile technique'],
          [],
          'Matched injection/aspiration performance documentation',
        ),
      ],
    },
  ],
};

const CPT_09880: CptProofMap = {
  cpt_code: '09880',
  cpt_description: 'Assistant surgeon modifier — documentation of medical necessity',
  version: 1,
  effective_date: '2024-01-01',
  requirement_elements: [
    {
      requirement_id: '09880_necessity',
      label: 'Assistant Surgeon Medical Necessity',
      description: 'Documentation that assistant surgeon was required due to complexity, duration, or critical portions',
      strictness: 'REQUIRED',
      evidence_rules: [
        keywordRule('09880_n1',
          ['assistant surgeon', 'assistant required', 'required due to complexity',
           'critical portions', 'needed for exposure', 'retraction', 'patient factors',
           'assistant necessary', 'complexity required assistant'],
          ['assistant not required', 'assistant present but not necessary', 'assistant not needed'],
          'Matched assistant surgeon necessity documentation'
        ),
      ],
    },
  ],
};

// ============================================================================
// FULL PROOF MAP REGISTRY
// ============================================================================

export const ORTHOPEDIC_PROOF_MAPS: CptProofMap[] = [
  CPT_29827,
  CPT_29826,
  CPT_29824,
  CPT_29828,
  CPT_29807,
  CPT_29822,
  CPT_29823,
  CPT_27570,
  CPT_27385,
  CPT_29880,
  CPT_29881,
  CPT_29882,
  CPT_29877,
  CPT_27447,
  CPT_27435,
  CPT_23420,
  CPT_23120,
  CPT_64721,
  CPT_20610,
  CPT_09880,
];

/**
 * Get a proof map by CPT code.
 */
export function getProofMap(cptCode: string): CptProofMap | undefined {
  return ORTHOPEDIC_PROOF_MAPS.find(pm => pm.cpt_code === cptCode);
}

/**
 * Get proof maps for a list of CPT codes.
 * Returns only those that have defined rules.
 */
export function getProofMapsForCpts(cptCodes: string[]): CptProofMap[] {
  return cptCodes
    .map(code => getProofMap(code))
    .filter((pm): pm is CptProofMap => pm !== undefined);
}
